import client, { mock, isRealApiEnabled } from "./client";
import productsMock, {
  netsuiteSearchResults,
  saveProducts,
  purchaseEvents,
  listProductTags,
  listProductTypes,
  normalizeTag,
  normalizeTags,
} from "./mocks/products";

export { normalizeTag, normalizeTags };

const STATUS_LABELS = {
  archived: "Archived",
  in_review: "In Review",
  scheduled: "Scheduled",
  published: "Published",
  // "draft" was retired as a publish status; legacy rows read as In Review.
  draft: "In Review",
};

// Multi-value sections (tag / membership / pin level) support a per-section
// match mode. "AND" => the product must carry every selected value; "OR" =>
// the product must carry at least one. Default is AND (matches prior behavior).
const multiMatch = (mode, list, predicate) =>
  String(mode).toUpperCase() === "OR"
    ? list.some(predicate)
    : list.every(predicate);

const matchesFilters = (product, filters = {}, filterModes = {}) => {
  return Object.entries(filters).every(([key, values]) => {
    const list = Array.isArray(values)
      ? values
      : values == null
      ? []
      : [values];
    // Membership / Pin Level are inclusion filters handled before the generic
    // "empty means no filter" guard below: every audience code a product
    // carries must still be checked ("included") for it to show. All codes are
    // checked by default so everything shows; unchecking a code hides products
    // available to that audience, and unchecking them all hides every product
    // that carries any code. Products with no codes always show.
    if (key === "membership") {
      return (product.audienceMembershipCodes || []).every((c) =>
        list.includes(c)
      );
    }
    if (key === "pinLevel") {
      return (product.audiencePinCodes || []).every((c) => list.includes(c));
    }
    if (!values || (Array.isArray(values) && values.length === 0)) return true;
    const mode = filterModes[key];
    switch (key) {
      case "type":
        // "WWG" matches first-party items; "3rd" (3rd Party) is a catch-all for
        // anything whose source isn't WWG, so selecting both returns everything.
        return list.some((v) =>
          v === "WWG" ? product.type === "WWG" : product.type !== "WWG"
        );
      case "productType":
        return list.includes(product.productType || product.category);
      case "category": {
        // Real backend contract: filter values are Category Builder *codes*
        // (e.g. "LITERATURE_BOOKS") matched against `storeAppCategories` OR
        // `category`. Mock products mostly carry display names ("Literature"),
        // so also match a top-level code against the normalized display name
        // (e.g. code "LITERATURE" matches category "Literature").
        const normCat = (v) =>
          String(v || "")
            .toUpperCase()
            .replace(/\s+/g, " ")
            .trim();
        const carried = [
          ...(product.storeAppCategories || []),
          product.category,
        ].map(normCat);
        return list.some((v) => carried.includes(normCat(v)));
      }
      case "language":
        return list.includes(product.language);
      case "status":
        return list.some(
          (v) => v === product.status || v === STATUS_LABELS[product.status]
        );
      case "availability": {
        // Availability is stored canonically lowercase (e.g. "outofstock") by
        // the editor and backend, but seed/mock data and older records may be
        // Title Case ("Out of Stock"). Normalize both sides so the filter
        // matches regardless of casing/spacing.
        const normAvail = (a) =>
          String(a || "")
            .toLowerCase()
            .replace(/[^a-z]/g, "");
        return list.some(
          (v) => normAvail(v) === normAvail(product.availability)
        );
      }
      case "tag": {
        const productTags = normalizeTags(product.tags || []);
        return multiMatch(mode, list, (v) =>
          productTags.includes(normalizeTag(v))
        );
      }
      default:
        return true;
    }
  });
};

const matchesSearch = (product, search) => {
  const q = String(search || "")
    .trim()
    .toLowerCase();
  if (!q) return true;
  // `sku:` prefix scopes the search to SKU fields only (e.g. "sku:WWG-TS").
  if (q.startsWith("sku:")) {
    const skuQ = q.slice(4).trim();
    if (!skuQ) return true;
    return `${product.sku || ""} ${product.netsuiteSku || ""}`
      .toLowerCase()
      .includes(skuQ);
  }
  const haystack = `${product.name || ""} ${product.sku || ""} ${
    product.netsuiteSku || ""
  } ${product.productType || ""}`.toLowerCase();
  return haystack.includes(q);
};

const DAY_MS = 24 * 60 * 60 * 1000;

// Sum purchased quantity per product within an optional time window (ms since
// epoch). windowDays = null means "all time".
const popularityScores = (windowDays) => {
  const since = windowDays ? Date.now() - windowDays * DAY_MS : 0;
  const scores = new Map();
  purchaseEvents.forEach((ev) => {
    if (new Date(ev.at).getTime() >= since) {
      scores.set(ev.productId, (scores.get(ev.productId) || 0) + (ev.qty || 1));
    }
  });
  return scores;
};

const applySort = (items, sort) => {
  const list = [...items];
  // Date sorts use createdAt ("Date Added") so editing a product doesn't jump
  // it around; "Last Updated" sorts use updatedAt explicitly.
  const createdOf = (p) => new Date(p.createdAt || p.updatedAt || 0).getTime();
  const updatedOf = (p) => new Date(p.updatedAt || p.createdAt || 0).getTime();
  const priceOf = (p) => (p.pricing && p.pricing.retailCents) ?? 0;
  switch (sort) {
    case "name_asc":
    case "az":
      list.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      break;
    case "name_desc":
    case "za":
      list.sort((a, b) => (b.name || "").localeCompare(a.name || ""));
      break;
    case "price_asc":
      list.sort((a, b) => priceOf(a) - priceOf(b));
      break;
    case "price_desc":
      list.sort((a, b) => priceOf(b) - priceOf(a));
      break;
    case "updated_asc":
      list.sort((a, b) => updatedOf(a) - updatedOf(b));
      break;
    case "updated_desc":
      list.sort((a, b) => updatedOf(b) - updatedOf(a));
      break;
    case "created_asc":
    case "date_asc":
      list.sort((a, b) => createdOf(a) - createdOf(b));
      break;
    case "popularity_all":
    case "popularity_7d":
    case "popularity_30d": {
      const windowDays =
        sort === "popularity_7d" ? 7 : sort === "popularity_30d" ? 30 : null;
      const scores = popularityScores(windowDays);
      // Most popular first; tie-break on newest so it's deterministic.
      list.sort(
        (a, b) =>
          (scores.get(b.id) || 0) - (scores.get(a.id) || 0) ||
          createdOf(b) - createdOf(a)
      );
      break;
    }
    case "created_desc":
    case "date":
    case "date_desc":
    default:
      list.sort((a, b) => createdOf(b) - createdOf(a));
      break;
  }
  return list;
};

/**
 * @endpoint GET /api/v1/products
 * @param {object} [params]
 * @param {string} [params.search]
 * @param {object} [params.filters]  { type, category, language, tag, status, availability }
 * @param {object} [params.filterModes]  per-section match mode, e.g. { tag: "AND"|"OR" }
 * @param {string} [params.sort]     "date" | "name_asc" | "name_desc"
 * @param {number} [params.page]
 * @param {number} [params.pageSize]
 * @returns {Promise<{ items: Product[], total: number, page: number }>}
 */
export function list(params = {}) {
  const {
    search,
    filters,
    filterModes,
    sort,
    page = 1,
    pageSize = 50,
  } = params;
  if (isRealApiEnabled()) {
    return client.get("/v1/products", {
      query: {
        search,
        sort,
        page,
        pageSize,
        filters: JSON.stringify(filters || {}),
        filterModes: JSON.stringify(filterModes || {}),
      },
    });
  }
  const filtered = productsMock.filter(
    (p) => matchesSearch(p, search) && matchesFilters(p, filters, filterModes)
  );
  const sorted = applySort(filtered, sort);
  const start = (page - 1) * pageSize;
  const items = sorted.slice(start, start + pageSize);
  return mock({ items, total: filtered.length, page });
}

/**
 * @endpoint GET /api/v1/products/tags
 * Unique tags across the catalog, for the "Filter by Tag" options.
 * @returns {Promise<string[]>}
 */
export function listTags() {
  if (isRealApiEnabled()) return client.get("/v1/products/tags");
  return mock(listProductTags());
}

/**
 * @endpoint GET /api/v1/products/types
 * Distinct product types across the catalog, for the "Filter by Type" options.
 * @returns {Promise<string[]>}
 */
export function listTypes() {
  if (isRealApiEnabled()) return client.get("/v1/products/types");
  return mock(listProductTypes());
}

/**
 * @endpoint GET /api/v1/products/:id
 * @returns {Promise<Product>}
 */
export function get(id) {
  if (isRealApiEnabled())
    return client.get(`/v1/products/${encodeURIComponent(id)}`);
  const found = productsMock.find((p) => p.id === id);
  if (!found) return Promise.reject(new Error(`Product not found: ${id}`));
  return mock(found);
}

/**
 * @endpoint POST /api/v1/products
 * Import from NetSuite SKU. Body: { netsuiteSku, netsuiteTitle }.
 * Server creates with `status: 'in_review'`.
 * @returns {Promise<Product>}
 */
export function create(payload = {}) {
  if (isRealApiEnabled()) return client.post("/v1/products", payload);
  const id = `prd_new_${Date.now()}`;
  const created = {
    ...productsMock[0],
    ...payload,
    id,
    sku: payload.netsuiteSku || payload.sku || "",
    name: payload.netsuiteTitle || payload.name || "",
    status: "in_review",
    statusLabel: STATUS_LABELS.in_review,
    tags: normalizeTags(payload.tags || productsMock[0].tags || []),
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
  productsMock.unshift(created);
  saveProducts();
  return mock(created);
}

/**
 * @endpoint PATCH /api/v1/products/:id
 * @returns {Promise<Product>}
 */
export function update(id, payload) {
  if (isRealApiEnabled())
    return client.patch(`/v1/products/${encodeURIComponent(id)}`, payload);
  const idx = productsMock.findIndex((p) => p.id === id);
  // Match the backend, which returns null (→ 404) for an unknown product
  // rather than fabricating a success against some other product.
  if (idx === -1) return mock(null);
  const base = productsMock[idx];
  const updated = {
    ...base,
    ...payload,
    id,
    updatedAt: new Date().toISOString(),
  };
  if (payload && "tags" in payload) {
    updated.tags = normalizeTags(payload.tags || []);
  }
  if (payload && "status" in payload) {
    const key = String(payload.status).toLowerCase().replace(/\s+/g, "_");
    const labels = {
      archived: "Archived",
      in_review: "In Review",
      scheduled: "Scheduled",
      published: "Published",
      draft: "In Review",
    };
    updated.statusLabel = labels[key] || payload.status;
  }
  if (idx !== -1) productsMock[idx] = updated;
  saveProducts();
  return mock(updated);
}

/**
 * @endpoint POST /api/v1/products/:id/archive
 * @returns {Promise<Product>}
 */
export function archive(id) {
  if (isRealApiEnabled())
    return client.post(`/v1/products/${encodeURIComponent(id)}/archive`);
  const idx = productsMock.findIndex((p) => p.id === id);
  if (idx === -1) return mock(null);
  productsMock[idx] = {
    ...productsMock[idx],
    status: "archived",
    statusLabel: "Archived",
  };
  saveProducts();
  return mock(productsMock[idx]);
}

/**
 * @endpoint POST /api/v1/products/:id/unarchive
 * NEW — restore an archived product to published status.
 * @returns {Promise<Product>}
 */
export function unarchive(id) {
  if (isRealApiEnabled())
    return client.post(`/v1/products/${encodeURIComponent(id)}/unarchive`);
  const idx = productsMock.findIndex((p) => p.id === id);
  if (idx === -1) return mock(null);
  productsMock[idx] = {
    ...productsMock[idx],
    status: "published",
    statusLabel: "Published",
    // Match the BFF: clear stale schedule dates so a past unpublishAt can't
    // immediately flip the product back to draft.
    publish: {
      ...(productsMock[idx].publish || {}),
      status: "published",
      publishAt: null,
      unpublishAt: null,
    },
  };
  saveProducts();
  return mock(productsMock[idx]);
}

/**
 * @endpoint POST /api/v1/products/:id/duplicate
 * NEW — clones an existing product as a new draft with status `in_review`.
 * @returns {Promise<Product>}
 */
export function duplicate(id) {
  if (isRealApiEnabled())
    return client.post(`/v1/products/${encodeURIComponent(id)}/duplicate`);
  const base = productsMock.find((p) => p.id === id);
  if (!base) return mock(null);
  const newId = `prd_dup_${Date.now()}`;
  const created = {
    ...base,
    id: newId,
    name: `${base.name} (Copy)`,
    status: "in_review",
    statusLabel: STATUS_LABELS.in_review,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
  productsMock.unshift(created);
  saveProducts();
  return mock(created);
}

/**
 * @endpoint DELETE /api/v1/products/:id
 * "Hide" — a soft delete. The backend marks deletedAt and excludes the product
 * from all catalog queries (including the archived view), so it disappears from
 * the admin UI entirely. It is NOT physically removed and can only be brought
 * back with backend assistance. Only offered on already-archived products.
 * @returns {Promise<{ id: string, deleted: true }>}
 */
export function remove(id) {
  if (isRealApiEnabled())
    return client.del(`/v1/products/${encodeURIComponent(id)}`);
  const idx = productsMock.findIndex((p) => p.id === id);
  if (idx === -1) return mock(null);
  productsMock.splice(idx, 1);
  saveProducts();
  return mock({ id, deleted: true });
}

// Exposed as `api.products.delete(id)` for callers; `delete` is a reserved word so
// the implementation is named `remove` and re-exported under the public name.
export { remove as delete };

/**
 * @endpoint GET /api/v1/products/search?q=
 * @returns {Promise<Product[]>}
 */
export function search(query) {
  if (isRealApiEnabled())
    return client.get("/v1/products/search", { query: { q: query } });
  const q = String(query || "").toLowerCase();
  return mock(
    productsMock.filter(
      (p) => p.name.toLowerCase().includes(q) || p.sku.toLowerCase().includes(q)
    )
  );
}

/**
 * @endpoint GET /api/v1/products/netsuite-search?q=
 * NEW — searches NetSuite SKUs not yet imported into Mongo. Used by the
 * Add Product modal. Returns `{ sku, title }[]`.
 * @returns {Promise<Array<{ sku: string, title: string }>>}
 */
export function searchNetSuite(query) {
  if (isRealApiEnabled())
    return client.get("/v1/products/netsuite-search", { query: { q: query } });
  const q = String(query || "")
    .trim()
    .toLowerCase();
  if (!q) return mock([]);
  return mock(
    netsuiteSearchResults.filter(
      (r) =>
        r.sku.toLowerCase().includes(q) || r.title.toLowerCase().includes(q)
    )
  );
}
